import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from './config';
import { useSearchParams } from 'react-router-dom';
import './Todo.css';

function Todo({ username, onLogout }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeSubmenu = searchParams.get('submenu') || 'todo';
  
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [newItemTitle, setNewItemTitle] = useState('');
  const [editingItem, setEditingItem] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editPriority, setEditPriority] = useState('medium');
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch items when submenu changes
  useEffect(() => {
    fetchItems();
  }, [activeSubmenu]);

  // Debounce search query - only fetch after user stops typing
  useEffect(() => {
    if (activeSubmenu !== 'changelog') {
      return; // Only debounce for changelog
    }
    
    const timeoutId = setTimeout(() => {
      fetchItems(true); // Pass true to indicate this is a search (no loading spinner)
    }, 500); // Wait 500ms after user stops typing

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const fetchItems = async (isSearch = false) => {
    try {
      // Only show loading spinner if not a search (to prevent flickering and losing focus)
      if (!isSearch) {
        setLoading(true);
      }
      const changelog = activeSubmenu === 'changelog';
      let url = `${API_URL}/portfolio/wip/?page_size=all&changelog=${changelog}`;
      
      // Add search query if provided (only for changelog)
      if (changelog && searchQuery.trim()) {
        url += `&search=${encodeURIComponent(searchQuery.trim())}`;
      }
      
      const response = await axios.get(url);
      const data = response.data.results || response.data;
      setItems(Array.isArray(data) ? data : []);
      setError(null);
    } catch (err) {
      if (err.response && err.response.status === 401) {
        onLogout();
        setError('Session expired. Please log in again.');
      } else {
        setError(`Error fetching ${activeSubmenu === 'changelog' ? 'changelog' : 'todo'} items: ` + err.message);
      }
      console.error(err);
    } finally {
      if (!isSearch) {
        setLoading(false);
      }
    }
  };

  const handleSubmenuChange = (submenu) => {
    setSearchParams({ submenu });
  };

  const handleAddItem = async () => {
    if (!newItemTitle.trim()) {
      return;
    }

    try {
      const response = await axios.post(`${API_URL}/portfolio/wip/`, {
        title: newItemTitle.trim(),
        description: '',
        is_completed: false,
        priority: 'medium',
        changelog: false
      });
      setItems([response.data, ...items]);
      setNewItemTitle('');
    } catch (err) {
      setError('Error adding item: ' + err.message);
      console.error(err);
    }
  };

  const handleToggleExpand = (itemId) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(itemId)) {
      newExpanded.delete(itemId);
      setEditingItem(null);
    } else {
      newExpanded.add(itemId);
      const item = items.find(i => i.id === itemId);
      if (item) {
        setEditTitle(item.title);
        setEditDescription(item.description || '');
        setEditPriority(item.priority || 'medium');
      }
    }
    setExpandedItems(newExpanded);
  };

  const handleToggleCompleted = async (item) => {
    try {
      const response = await axios.patch(`${API_URL}/portfolio/wip/${item.id}/`, {
        is_completed: !item.is_completed
      });
      setItems(items.map(i => i.id === item.id ? response.data : i));
    } catch (err) {
      setError('Error updating item: ' + err.message);
      console.error(err);
    }
  };

  const handleUpdatePriority = async (item, newPriority) => {
    try {
      const response = await axios.patch(`${API_URL}/portfolio/wip/${item.id}/`, {
        priority: newPriority
      });
      setItems(items.map(i => i.id === item.id ? response.data : i));
    } catch (err) {
      setError('Error updating priority: ' + err.message);
      console.error(err);
    }
  };

  const handleCloseItem = async (item) => {
    if (!item.is_completed) {
      alert('Item must be completed before it can be closed');
      return;
    }

    if (!window.confirm('Are you sure you want to close this item? It will be moved to changelog.')) {
      return;
    }

    try {
      const response = await axios.patch(`${API_URL}/portfolio/wip/${item.id}/`, {
        changelog: true
      });
      // Remove from current list
      setItems(items.filter(i => i.id !== item.id));
      // If we're on changelog view, add it
      if (activeSubmenu === 'changelog') {
        setItems([response.data, ...items]);
      }
    } catch (err) {
      setError('Error closing item: ' + err.message);
      console.error(err);
    }
  };

  const handleSaveEdit = async (itemId) => {
    try {
      const response = await axios.patch(`${API_URL}/portfolio/wip/${itemId}/`, {
        title: editTitle.trim(),
        description: editDescription.trim(),
        priority: editPriority
      });
      setItems(items.map(i => i.id === itemId ? response.data : i));
      setEditingItem(null);
    } catch (err) {
      setError('Error saving item: ' + err.message);
      console.error(err);
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this item?')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/portfolio/wip/${itemId}/`);
      setItems(items.filter(i => i.id !== itemId));
      const newExpanded = new Set(expandedItems);
      newExpanded.delete(itemId);
      setExpandedItems(newExpanded);
    } catch (err) {
      setError('Error deleting item: ' + err.message);
      console.error(err);
    }
  };

  const handleStartEdit = (item) => {
    setEditingItem(item.id);
    setEditTitle(item.title);
    setEditDescription(item.description || '');
    setEditPriority(item.priority || 'medium');
  };

  const handleCancelEdit = () => {
    setEditingItem(null);
    const item = items.find(i => i.id === editingItem);
    if (item) {
      setEditTitle(item.title);
      setEditDescription(item.description || '');
      setEditPriority(item.priority || 'medium');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('nl-NL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDateOnly = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('nl-NL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return '#d32f2f';
      case 'medium':
        return '#ff9800';
      case 'low':
        return '#4caf50';
      default:
        return '#666';
    }
  };

  const isChangelogView = activeSubmenu === 'changelog';

  return (
    <div className="todo-container">
      <div className="todo-header">
        <h2>Todo</h2>
        <p>Manage your tasks and view completed items</p>
      </div>

      {/* Submenu */}
      <div className="todo-submenu">
        <button
          className={`todo-submenu-button ${activeSubmenu === 'todo' ? 'active' : ''}`}
          onClick={() => handleSubmenuChange('todo')}
        >
          Todo
        </button>
        <button
          className={`todo-submenu-button ${activeSubmenu === 'changelog' ? 'active' : ''}`}
          onClick={() => handleSubmenuChange('changelog')}
        >
          Changelog
        </button>
      </div>

      <div className="todo-content">
        {loading ? (
          <div className="loading">Loading...</div>
        ) : error ? (
          <div className="error">{error}</div>
        ) : (
          <>
            {!isChangelogView && (
              <div className="todo-add-section">
                <input
                  type="text"
                  className="todo-add-input"
                  placeholder="Add new item..."
                  value={newItemTitle}
                  onChange={(e) => setNewItemTitle(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleAddItem();
                    }
                  }}
                />
                <button className="todo-add-button" onClick={handleAddItem}>
                  Add
                </button>
              </div>
            )}

            {isChangelogView ? (
              <>
                <div className="changelog-search-container">
                  <input
                    type="text"
                    className="changelog-search-input"
                    placeholder="Zoek in titel of beschrijving..."
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                    }}
                    autoComplete="off"
                  />
                </div>
                {items.length === 0 ? (
                  <div className="todo-empty-state">
                    {searchQuery.trim() 
                      ? `Geen resultaten gevonden voor "${searchQuery}"`
                      : 'No items in changelog yet. Close completed items to add them here.'
                    }
                  </div>
                ) : (
                  <div className="trades-table-container">
                    <table className="trades-table todo-changelog-table">
                  <thead>
                    <tr>
                      <th style={{ width: '20%' }}>Date</th>
                      <th style={{ width: '60%' }}>Title</th>
                      <th style={{ width: '20%', textAlign: 'right' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => {
                      const isExpanded = expandedItems.has(item.id);
                      return (
                        <React.Fragment key={item.id}>
                          <tr 
                            className={isExpanded ? 'expanded' : ''}
                            onClick={() => handleToggleExpand(item.id)}
                            style={{ cursor: 'pointer' }}
                          >
                            <td className="date-cell" style={{ width: '20%' }}>
                              {formatDateOnly(item.updated_at)}
                            </td>
                            <td className="symbol-cell" style={{ width: '60%' }}>
                              {item.title}
                            </td>
                            <td style={{ width: '20%', textAlign: 'right' }}>
                              <button
                                className="todo-delete-button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteItem(item.id);
                                }}
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr>
                              <td colSpan="3" style={{ padding: '1rem', backgroundColor: '#fafafa', borderTop: '1px solid #e0e0e0' }}>
                                <div style={{ color: '#666', lineHeight: '1.6' }}>
                                  {item.description ? (
                                    <p style={{ margin: 0 }}>{item.description}</p>
                                  ) : (
                                    <p className="todo-no-description" style={{ margin: 0 }}>No description</p>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                    </tbody>
                  </table>
                  </div>
                )}
              </>
            ) : items.length === 0 ? (
              <div className="todo-empty-state">
                No items yet. Add your first item above!
              </div>
            ) : (
              <div className="todo-items-list">
                {items.map((item) => {
                  const isExpanded = expandedItems.has(item.id);
                  const isEditing = editingItem === item.id && !isChangelogView;

                  return (
                    <div
                      key={item.id}
                      className={`todo-item ${item.is_completed ? 'completed' : ''} ${isExpanded ? 'expanded' : ''}`}
                    >
                      <div className="todo-item-header" onClick={() => handleToggleExpand(item.id)}>
                        <div className="todo-item-checkbox-wrapper">
                          <input
                            type="checkbox"
                            className="todo-item-checkbox"
                            checked={item.is_completed}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleToggleCompleted(item);
                            }}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                        <div className="todo-item-title-wrapper">
                          {isEditing ? (
                            <input
                              type="text"
                              className="todo-edit-title-input"
                              value={editTitle}
                              onChange={(e) => setEditTitle(e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                  handleSaveEdit(item.id);
                                }
                              }}
                            />
                          ) : (
                            <span className="todo-item-title">{item.title}</span>
                          )}
                        </div>
                        <div className="todo-item-priority">
                          {!isEditing ? (
                            <select
                              className="todo-priority-select"
                              value={item.priority || 'medium'}
                              onChange={(e) => {
                                e.stopPropagation();
                                handleUpdatePriority(item, e.target.value);
                              }}
                              onClick={(e) => e.stopPropagation()}
                              style={{ borderLeftColor: getPriorityColor(item.priority || 'medium') }}
                            >
                              <option value="low">Low</option>
                              <option value="medium">Medium</option>
                              <option value="high">High</option>
                            </select>
                          ) : (
                            <select
                              className="todo-priority-select"
                              value={editPriority}
                              onChange={(e) => setEditPriority(e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                              style={{ borderLeftColor: getPriorityColor(editPriority) }}
                            >
                              <option value="low">Low</option>
                              <option value="medium">Medium</option>
                              <option value="high">High</option>
                            </select>
                          )}
                        </div>
                        <div className="todo-item-actions">
                          {isExpanded && !isEditing && (
                            <button
                              className="todo-edit-button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleStartEdit(item);
                              }}
                            >
                              Edit
                            </button>
                          )}
                          {isExpanded && isEditing && (
                            <>
                              <button
                                className="todo-save-button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSaveEdit(item.id);
                                }}
                              >
                                Save
                              </button>
                              <button
                                className="todo-cancel-button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCancelEdit();
                                }}
                              >
                                Cancel
                              </button>
                            </>
                          )}
                          {item.is_completed && (
                            <button
                              className="todo-close-button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCloseItem(item);
                              }}
                              title="Close item (move to changelog)"
                            >
                              Close
                            </button>
                          )}
                          <button
                            className="todo-delete-button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteItem(item.id);
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      </div>

                      {isExpanded && (
                        <div className="todo-item-details" onClick={(e) => e.stopPropagation()}>
                          {isEditing ? (
                            <textarea
                              className="todo-edit-description-input"
                              value={editDescription}
                              onChange={(e) => setEditDescription(e.target.value)}
                              placeholder="Description (optional)"
                              rows="4"
                            />
                          ) : (
                            <div className="todo-item-description">
                              {item.description ? (
                                <p>{item.description}</p>
                              ) : (
                                <p className="todo-no-description">No description</p>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default Todo;

